import { useDeferredValue, useEffect, useMemo, useState } from "react";
import { Search, SlidersHorizontal, Loader2, Tag, Layers, X, Sparkles, ArrowRight } from "lucide-react";
import { Link, useSearchParams } from "react-router-dom";
import { Header } from "../components/Header";
import { CartSheet } from "../components/CartSheet";
import { ProductCard } from "../components/ProductCard";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from "../components/ui/select";
import { clientApi } from "../lib/api";
import { stripAccents } from "../lib/searchUtils";
import { useIncrementalList } from "../lib/useIncrementalList";
import { CarteraSummaryCard } from "../components/CarteraSummaryCard";
import { OnboardingTour, shouldShowTourAuto } from "../components/OnboardingTour";
import { ChatWidget } from "../components/ChatWidget";
import { useSession } from "../context/SessionContext";
import { toast } from "sonner";

// Jerarquía real de Arcos: Línea → Sublínea (ProductoLineaN2) → Sub sublínea (N3).
// No hay mapeo manual: lo que está en el ERP es lo que ve el cliente.
const LINEA_ORDER = [
  "ALIMENTOS",
  "BEBIDAS",
  "ASEO Y LIMPIEZA",
  "PAPEL E HIGIENE",
  "CUIDADO PERSONAL",
  "EMPAQUES Y DESECHABLES",
  "HOGAR Y UTILIDADES",
  "MASCOTAS",
];

const lineaRank = (l) => {
  const i = LINEA_ORDER.indexOf(l);
  return i === -1 ? LINEA_ORDER.length : i;
};

export default function Catalog() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [products, setProducts] = useState([]);
  const [suggested, setSuggested] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [activeSection, setActiveSection] = useState("Todos");
  const [activeSublinea, setActiveSublinea] = useState("Todas");
  const [activeMarca, setActiveMarca] = useState(searchParams.get("marca") || "Todas");
  const [activeProveedor, setActiveProveedor] = useState(searchParams.get("proveedor") || "Todos");
  const [cartera, setCartera] = useState(null);
  const [tourOpen, setTourOpen] = useState(false);
  const { customer, mustChangePassword } = useSession();

  // Sync URL query params <-> filter state. Handles both directions:
  //  - Landing with ?marca=X or navigating from a modal → applies filter
  //  - User changes filter with dropdown → updates URL so it's shareable
  useEffect(() => {
    const urlMarca = searchParams.get("marca") || "Todas";
    const urlProv = searchParams.get("proveedor") || "Todos";
    if (urlMarca !== activeMarca) setActiveMarca(urlMarca);
    if (urlProv !== activeProveedor) setActiveProveedor(urlProv);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  useEffect(() => {
    const params = {};
    if (activeMarca !== "Todas") params.marca = activeMarca;
    if (activeProveedor !== "Todos") params.proveedor = activeProveedor;
    setSearchParams(params, { replace: true });
  }, [activeMarca, activeProveedor, setSearchParams]);

  useEffect(() => {
    clientApi.get("/portal/products")
      .then((r) => setProducts(r.data || []))
      .catch(() => toast.error("Error al cargar productos"))
      .finally(() => setLoading(false));
    clientApi.get("/portal/cartera")
      .then((r) => setCartera(r.data || null))
      .catch(() => {});
    // Load suggested in parallel (top 6 favorites from order history)
    clientApi.get("/portal/favorites?limit=12")
      .then((r) => {
        const items = (r.data || []).filter((p) => p.disponible && p.precio);
        // Sort by urgency: overdue first, then due, then on-track
        const urgencyScore = (p) => {
          const c = p.cadencia_dias;
          const d = p.dias_desde_ultimo;
          if (!c || d === null || d === undefined) return 0;
          if (d > c * 1.5 && d >= 7) return 3;  // overdue
          if (d >= c * 0.85) return 2;            // due soon
          return 1;                                // on-track
        };
        items.sort((a, b) => {
          const ua = urgencyScore(a);
          const ub = urgencyScore(b);
          if (ua !== ub) return ub - ua;
          return (b.veces_pedido || 0) - (a.veces_pedido || 0);
        });
        setSuggested(items.slice(0, 8));
      })
      .catch(() => {});
  }, []);

  // Auto-open the onboarding tour on the client's first visit.
  // Server-side truth (client_state) trumps localStorage so it doesn't
  // reappear on other devices/browsers.
  // If the client is being forced to change password, defer the tour so
  // both dialogs don't stack on top of each other — it will re-check
  // once the password modal is resolved.
  useEffect(() => {
    if (!shouldShowTourAuto() || mustChangePassword) return;
    let timeoutId;
    clientApi.get("/portal/client-state")
      .then(({ data }) => {
        if (data?.onboarding_completed) {
          try { localStorage.setItem("cm_tour_completed", "1"); } catch { /* ignore */ }
        } else {
          timeoutId = setTimeout(() => setTourOpen(true), 900);
        }
      })
      .catch(() => {
        timeoutId = setTimeout(() => setTourOpen(true), 900);
      });
    return () => { if (timeoutId) clearTimeout(timeoutId); };
  }, [mustChangePassword]);

  // Enrich each product with section
  // Pre-compute a lowercase, accent-stripped search haystack once per product
  // so mobile users can type "panal" and find "pañal" (or vice versa) without
  // worrying about the ñ / accents. Runs once per products load.
  const enriched = useMemo(
    () => products.map((p) => {
      const section = (p.linea || p.categoria || "OTROS").trim();
      const sublinea = (p.sublinea || "").trim();
      const haystack = stripAccents(
        `${p.descripcion || ""} ${p.codigo || ""} ${p.marca || ""} ` +
        `${p.proveedor || ""} ${section} ${sublinea} ${p.sub_sublinea || ""}`
      ).toLowerCase();
      return { ...p, _section: section, _sublinea: sublinea, _search: haystack };
    }),
    [products]
  );

  const sectionCounts = useMemo(() => {
    const map = { Todos: enriched.length };
    enriched.forEach((p) => { map[p._section] = (map[p._section] || 0) + 1; });
    return map;
  }, [enriched]);

  const sections = useMemo(() => {
    const known = Object.keys(sectionCounts).filter((s) => s !== "Todos");
    known.sort((a, b) => lineaRank(a) - lineaRank(b) || a.localeCompare(b));
    return ["Todos", ...known];
  }, [sectionCounts]);

  // Sublíneas disponibles dentro de la línea activa, con su conteo.
  const sublineas = useMemo(() => {
    const map = new Map();
    enriched.forEach((p) => {
      if (activeSection !== "Todos" && p._section !== activeSection) return;
      if (!p._sublinea) return;
      map.set(p._sublinea, (map.get(p._sublinea) || 0) + 1);
    });
    return Array.from(map.entries()).sort((a, b) => a[0].localeCompare(b[0]));
  }, [enriched, activeSection]);

  // Available brands within the active section + proveedor
  const marcas = useMemo(() => {
    const set = new Set();
    enriched.forEach((p) => {
      if (activeSection !== "Todos" && p._section !== activeSection) return;
      if (activeProveedor !== "Todos" && p.proveedor !== activeProveedor) return;
      if (p.marca) set.add(p.marca);
    });
    return ["Todas", ...Array.from(set).sort()];
  }, [enriched, activeSection, activeProveedor]);

  const proveedores = useMemo(() => {
    const set = new Set();
    enriched.forEach((p) => {
      if (activeSection !== "Todos" && p._section !== activeSection) return;
      if (p.proveedor) set.add(p.proveedor);
    });
    return ["Todos", ...Array.from(set).sort()];
  }, [enriched, activeSection]);

  // Keep the input instantly responsive: React renders the keystroke first and
  // re-filters the 2.7k catalog at low priority (React 19 concurrent).
  const debouncedSearch = useDeferredValue(search);

  const filtered = useMemo(() => {
    const q = stripAccents(debouncedSearch.trim().toLowerCase());
    return enriched.filter((p) => {
      if (activeSection !== "Todos" && p._section !== activeSection) return false;
      if (activeSublinea !== "Todas" && p._sublinea !== activeSublinea) return false;
      if (activeMarca !== "Todas" && p.marca !== activeMarca) return false;
      if (activeProveedor !== "Todos" && p.proveedor !== activeProveedor) return false;
      if (!q) return true;
      return p._search.includes(q);
    });
  }, [enriched, debouncedSearch, activeSection, activeSublinea, activeMarca, activeProveedor]);

  // Render in chunks — never mount 1.8k cards at once (mobile perf).
  const { visible, shown, hasMore, sentinelRef } = useIncrementalList(filtered, {
    step: 48,
    resetKey: `${debouncedSearch}|${activeSection}|${activeSublinea}|${activeMarca}|${activeProveedor}`,
  });

  const hasActiveFilters = activeSection !== "Todos" || activeSublinea !== "Todas" || activeMarca !== "Todas" || activeProveedor !== "Todos" || search.trim();
  const clearFilters = () => {
    setSearch("");
    setActiveSection("Todos");
    setActiveSublinea("Todas");
    setActiveMarca("Todas");
    setActiveProveedor("Todos");
  };

  return (
    <div className="min-h-screen bg-cm-cream" data-testid="catalog-page">
      <Header onOpenTour={() => setTourOpen(true)} />
      <CartSheet />
      <OnboardingTour open={tourOpen} onClose={() => setTourOpen(false)} />

      <div className="bg-white border-b border-cm-border">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
            <div>
              <p className="text-xs uppercase tracking-widest text-cm-ink-soft font-medium">Catálogo</p>
              <h1 className="font-serif text-3xl sm:text-4xl mt-1 tracking-tight">Productos disponibles</h1>
              <p className="text-sm text-cm-ink-soft mt-1">
                Tus precios personalizados. Solo se muestran productos con disponibilidad.
              </p>
            </div>
            <div className="relative w-full sm:w-80">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-cm-ink-soft" />
              <Input
                placeholder="Buscar por nombre, marca o código..."
                className="pl-9 h-11 border-cm-border focus-visible:ring-cm-red focus-visible:ring-offset-0"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                data-testid="search-input"
              />
            </div>
          </div>

          <div className="mt-6 flex items-center gap-2 overflow-x-auto pb-1 -mx-1 px-1" data-testid="section-filters">
            <SlidersHorizontal className="h-4 w-4 text-cm-ink-soft flex-shrink-0" />
            {sections.map((sec) => {
              const isActive = activeSection === sec;
              const count = sectionCounts[sec] || 0;
              if (sec !== "Todos" && count === 0) return null;
              const slug = sec.toLowerCase().replace(/\s+/g, "-").replace(/[^a-z0-9-]/g, "");
              return (
                <button
                  key={sec}
                  onClick={() => { setActiveSection(sec); setActiveSublinea("Todas"); setActiveMarca("Todas"); setActiveProveedor("Todos"); }}
                  className={`flex-shrink-0 px-3.5 py-1.5 rounded-md text-sm font-medium border transition-all whitespace-nowrap ${
                    isActive
                      ? "bg-cm-red text-white border-cm-red"
                      : "bg-white text-cm-ink border-cm-border hover:border-cm-ink/30"
                  }`}
                  data-testid={`section-filter-${slug}`}
                >
                  {sec} <span className="ml-1 opacity-70 font-mono text-xs">{count}</span>
                </button>
              );
            })}
          </div>

          <div className="mt-3 flex items-center gap-3 flex-wrap">
            {sublineas.length > 0 && (
              <>
                <div className="flex items-center gap-2">
                  <Layers className="h-4 w-4 text-cm-ink-soft" />
                  <span className="text-xs text-cm-ink-soft uppercase tracking-widest font-medium">Sublínea</span>
                </div>
                <Select value={activeSublinea} onValueChange={setActiveSublinea}>
                  <SelectTrigger className="w-60 h-9 border-cm-border" data-testid="sublinea-filter">
                    <SelectValue placeholder="Todas las sublíneas" />
                  </SelectTrigger>
                  <SelectContent className="max-h-80">
                    <SelectItem value="Todas" data-testid="sublinea-option-todas">
                      Todas las sublíneas
                    </SelectItem>
                    {sublineas.map(([name, count]) => (
                      <SelectItem
                        key={name}
                        value={name}
                        data-testid={`sublinea-option-${name.replace(/\W/g, "_").toLowerCase()}`}
                      >
                        {name} ({count})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </>
            )}
            <div className="flex items-center gap-2">
              <Tag className="h-4 w-4 text-cm-ink-soft" />
              <span className="text-xs text-cm-ink-soft uppercase tracking-widest font-medium">Marca</span>
            </div>
            <Select value={activeMarca} onValueChange={setActiveMarca}>
              <SelectTrigger className="w-52 h-9 border-cm-border" data-testid="brand-filter">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="max-h-80">
                {marcas.map((m) => (
                  <SelectItem key={m} value={m} data-testid={`brand-option-${m.replace(/\W/g,"_").toLowerCase()}`}>{m}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            {hasActiveFilters && (
              <button
                onClick={clearFilters}
                className="text-xs text-cm-red hover:underline inline-flex items-center"
                data-testid="clear-filters-btn"
              >
                <X className="h-3 w-3 mr-1" /> Limpiar filtros
              </button>
            )}
          </div>
        </div>
      </div>

      <main className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-8">
        {/* Cartera summary card — visible on top so client always sees their credit status */}
        {cartera && cartera.cupo > 0 && (
          <div className="mb-6" data-testid="cartera-section">
            <CarteraSummaryCard cartera={cartera} customer={customer} />
          </div>
        )}

        {/* Sugeridos para ti — distinct card with warm background to stand out from the catalog */}
        {!loading && suggested.length > 0 && !hasActiveFilters && (
          <section
            className="mb-8 -mx-4 sm:mx-0 sm:rounded-2xl bg-gradient-to-br from-cm-yellow/10 via-cm-cream-alt/80 to-cm-cream-alt/40 border-y sm:border border-cm-yellow/40 px-4 sm:px-6 py-5 shadow-[0_1px_0_rgba(0,0,0,0.02)]"
            data-testid="suggested-section"
          >
            <div className="flex items-center justify-between mb-3 gap-3">
              <div className="flex items-center gap-2">
                <div className="inline-flex items-center justify-center h-7 w-7 rounded-full bg-cm-yellow/40 ring-1 ring-cm-yellow/50">
                  <Sparkles className="h-3.5 w-3.5 text-amber-700" />
                </div>
                <h2 className="font-serif text-lg tracking-tight">Sugeridos para ti</h2>
                <span className="text-xs text-cm-ink-soft hidden sm:inline">· tus productos frecuentes</span>
              </div>
              <Link
                to="/sugeridos"
                className="text-xs text-cm-red hover:underline inline-flex items-center gap-1 flex-shrink-0 font-medium"
                data-testid="see-all-suggestions-link"
              >
                Ver todos <ArrowRight className="h-3 w-3" />
              </Link>
            </div>
            <div
              className="flex gap-3 overflow-x-auto pb-3 -mx-1 px-1 snap-x snap-mandatory"
              data-testid="suggested-strip"
            >
              {suggested.map((p) => {
                // Reposition cadence label: only if we have a meaningful signal
                let cadenceLabel = null;
                let cadenceTone = "neutral"; // neutral | due | overdue
                const c = p.cadencia_dias;
                const d = p.dias_desde_ultimo;
                if (c && c > 0 && d !== null && d !== undefined) {
                  if (d > c * 1.5 && d >= 7) {
                    cadenceLabel = `Hace ${d} días sin pedir`;
                    cadenceTone = "overdue";
                  } else if (d >= c * 0.85) {
                    cadenceLabel = `Toca repedir (cada ~${c} días)`;
                    cadenceTone = "due";
                  } else if (c <= 60) {
                    cadenceLabel = `Pides cada ~${c} días`;
                  }
                } else if (p.veces_pedido && p.veces_pedido >= 3) {
                  cadenceLabel = `Pedido ${p.veces_pedido} veces`;
                }
                const cadenceClass = cadenceTone === "overdue"
                  ? "bg-cm-red text-white"
                  : cadenceTone === "due"
                    ? "bg-cm-yellow text-cm-ink"
                    : "bg-white/80 text-cm-ink-soft border border-cm-border/70";
                return (
                  <div
                    key={`sug-${p.codigo}`}
                    className="flex-shrink-0 w-[160px] sm:w-[180px] snap-start"
                    data-testid={`suggested-${p.codigo}`}
                  >
                    <ProductCard product={p} />
                    {cadenceLabel && (
                      <p
                        className={`mt-1.5 inline-flex items-center gap-1 text-[10px] font-medium uppercase tracking-wider px-2 py-1 rounded ${cadenceClass}`}
                        data-testid={`cadence-${p.codigo}`}
                      >
                        {cadenceTone === "overdue" && <span>⚠️</span>}
                        {cadenceLabel}
                      </p>
                    )}
                  </div>
                );
              })}
            </div>
          </section>
        )}

        {loading ? (
          <div className="text-center py-24 text-cm-ink-soft">
            <Loader2 className="h-8 w-8 mx-auto animate-spin mb-3" />
            <p>Cargando catálogo...</p>
          </div>
        ) : products.length === 0 ? (
          <div className="text-center py-24 text-cm-ink-soft" data-testid="empty-catalog">
            <p className="font-serif text-2xl mb-2">Catálogo en preparación</p>
            <p className="text-sm max-w-md mx-auto">
              Aún no tenemos productos disponibles para tu lista de precios. Contáctanos para
              ayudarte: <span className="font-mono text-cm-red">304 1117 453</span>.
            </p>
          </div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-24 text-cm-ink-soft" data-testid="empty-results">
            <p className="font-serif text-2xl mb-2">Sin resultados</p>
            <p className="text-sm">Intenta otra búsqueda o cambia de filtros.</p>
          </div>
        ) : (
          <>
            <div className="mb-4 flex items-center justify-between text-sm text-cm-ink-soft">
              <span data-testid="result-count">
                {filtered.length} productos{hasMore ? " " : ""}
                {hasMore && <span className="opacity-60">· mostrando {shown}</span>}              </span>
              <div className="flex gap-2">
                {activeSection !== "Todos" && (
                  <Badge variant="outline" className="border-cm-yellow bg-cm-yellow/20 text-cm-ink">{activeSection}</Badge>
                )}
                {activeSublinea !== "Todas" && (
                  <Badge variant="outline" className="border-cm-ink/30 bg-white text-cm-ink" data-testid="active-sublinea-badge">{activeSublinea}</Badge>
                )}
                {activeMarca !== "Todas" && (
                  <Badge variant="outline" className="border-cm-red bg-cm-red/10 text-cm-red">{activeMarca}</Badge>
                )}
                {activeProveedor !== "Todos" && (
                  <Badge variant="outline" className="border-cm-ink bg-cm-cream-alt text-cm-ink">{activeProveedor}</Badge>
                )}
              </div>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3 sm:gap-4" data-testid="product-grid">
              {visible.map((p) => <ProductCard key={p.codigo} product={p} />)}
            </div>
            {hasMore && (
              <div ref={sentinelRef} className="py-10 flex flex-col items-center gap-2" data-testid="catalog-load-more">
                <Loader2 className="h-5 w-5 animate-spin text-cm-ink-soft" />
                <span className="text-xs text-cm-ink-soft uppercase tracking-widest">Cargando más productos</span>
              </div>
            )}
          </>
        )}
      </main>
      <ChatWidget />
    </div>
  );
}
